import React, { useState } from 'react'
import {ArrowLeft, Star, Calendar, Users, Heart} from 'lucide-react'
import { Link } from 'react-router-dom'
import { RatingSystem } from '../components/RatingSystem'
import { SocialShare } from '../components/SocialShare'
import { CommentSection } from '../components/CommentSection'
import { UserAuth } from '../components/UserAuth'
import { useAuth } from '../context/AuthContext'

export function TomJerryMagicRing() {
  const { user, toggleFavorite, isFavorite } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const contentId = 'tom-jerry-magic-ring'
  const favorite = isFavorite(contentId)

  const handleFavoriteClick = () => {
    if (!user) {
      setShowAuthModal(true)
      return
    }
    toggleFavorite(contentId)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-600 mb-8 font-semibold">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <article className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <img 
            src="https://static.lumi.new/67/678dc25a595a0cdc8d11f06c2bbd13e0.png" 
            alt="Tom and Jerry Magic Ring"
            className="w-full h-96 object-cover"
          />
          
          <div className="p-8 sm:p-12">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <span className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                FAN FAVORITE
              </span>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>2002</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4" />
                <span>Family Friendly</span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-6">
              Tom & Jerry Magic Ring
            </h1>

            <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
              <p className="text-xl leading-relaxed">
                <strong>Tom and Jerry Magic Ring</strong> is a timeless animated masterpiece that combines 
                the classic cat-and-mouse rivalry with enchanting magical elements. This direct-to-video 
                film brings unforgettable chaos and laughter to families worldwide.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">The Magical Plot</h2>
              <p>
                When Jerry accidentally gets a magical ring stuck on his head, the hilarious chase begins! 
                The <strong>Tom and Jerry Ring</strong> chase takes our beloved duo through a series of 
                wild adventures as they try to remove the enchanted jewelry. The ring grants wishes but 
                comes with unpredictable consequences.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Why It's Magical</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Classic slapstick comedy meets fantasy adventure</li>
                <li>Stunning animation that captures the original series' charm</li>
                <li>Perfect blend of humor and magical mayhem</li>
                <li>Memorable supporting characters including a young wizard</li>
                <li>Family-friendly entertainment for all ages</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Tom and Jerry Magic Elements</h2>
              <p>
                The film showcases incredible <strong>Tom and Jerry Magic</strong> sequences that add 
                a fresh twist to the classic formula. From transformation spells to magical mishaps, 
                every scene delivers both laughs and wonder. The magical ring becomes more than just 
                a MacGuffin—it's the catalyst for some of the most creative gags in the franchise.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Our Verdict</h2>
              <p>
                <strong>Tom & Jerry Magic Ring</strong> is a must-watch for fans of the original series 
                and newcomers alike. It perfectly captures the spirit of the classic cartoons while 
                introducing magical elements that keep the story fresh and exciting. The film proves 
                that even after decades, Tom and Jerry's chemistry remains timeless.
              </p>

              <div className="bg-gradient-to-r from-pink-100 to-orange-100 rounded-2xl p-6 mt-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Quick Facts</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Release Year:</strong> 2002</li>
                  <li><strong>Runtime:</strong> 62 minutes</li>
                  <li><strong>Genre:</strong> Animated Comedy, Fantasy</li>
                  <li><strong>Best For:</strong> Family movie night, Kids 5+</li>
                </ul>
              </div>
            </div>

            {/* Social Share and Favorite */}
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <button
                onClick={handleFavoriteClick}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 ${
                  favorite
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Heart className={`w-5 h-5 ${favorite ? 'fill-white' : ''}`} />
                {favorite ? 'Favorited' : 'Add to Favorites'}
              </button>
              <SocialShare title="Tom & Jerry Magic Ring" />
            </div>
          </div>
        </article>

        {/* Rating System */}
        <div className="mt-8">
          <RatingSystem contentTitle="Tom & Jerry Magic Ring" />
        </div>

        {/* Comment Section */}
        <div className="mt-8">
          <CommentSection 
            contentId={contentId}
            onAuthRequired={() => setShowAuthModal(true)}
          />
        </div>
      </div>

      {/* Auth Modal */}
      <UserAuth isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  )
}
